import React, {FC} from 'react';
import styled from 'styled-components';

import {DropdownItemNestedIcon} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemNestedIcon';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getColorHighlight, getEmojiHighlight} from '../../../helpers/models/highlightHelpers';
import {TagBehaviorsEnum, TagModel} from '../../../models/api/tagModel';
import {getStarredTagIds} from '../../../store/data/tags/tagsSelectors';
import {Emoji} from '../emoji/emoji';

/*
 * Props.
 */

export interface DropdownItemTagIconProps {
  tag: TagModel;
  nestingLevel: number;
}

/*
 * Style.
 */

const StyledEmojiDiv = styled.div`
  ${center()};
  width: 20px;
`;

/*
 * Component.
 */

export const DropdownItemTagIcon: FC<DropdownItemTagIconProps> = (props) => {
  const starredTagIds = useAppSelector(getStarredTagIds);

  return (
    <DropdownItemNestedIcon nestingLevel={props.nestingLevel}>
      {renderTagIcon(props.tag, {isStarredTag: starredTagIds.has(props.tag.id)})}
    </DropdownItemNestedIcon>
  );
};

// TODO: Add accessible label.

interface RenderTagOptions {
  isSmall?: boolean;
  isStarredTag?: boolean;
}

export function renderTagIcon(tag: TagModel, {isStarredTag = false, isSmall}: RenderTagOptions = {}) {
  // Starred tag.
  if (isStarredTag) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <Icon name={isSmall ? 'starFilledSmall' : 'starFilled'} color={theme.palette.yellow.shade40} />
        )}
      </UserThemeConsumer>
    );
  }

  // Inbox tag.
  if (tag.isMainInboxTag) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name={isSmall ? 'inboxFilledSmall' : 'inboxFilled'} color={theme.greys.shade60} />}
      </UserThemeConsumer>
    );
  }

  // View tag.
  if (tag.behaviors.includes(TagBehaviorsEnum.CAN_CUSTOM_VIEW)) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name={isSmall ? 'customViewsSmall' : 'customViews'} color={theme.greys.shade60} />}
      </UserThemeConsumer>
    );
  }

  // Emoji highlight.
  const emoji = getEmojiHighlight(tag);
  if (emoji) {
    return (
      <StyledEmojiDiv>
        <Emoji emoji={emoji.emoji} skinTone={emoji.skinTone} size={isSmall ? 16 : 18} />
      </StyledEmojiDiv>
    );
  }

  // Default tag icon.
  const color = getColorHighlight(tag);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Icon
          name={isSmall ? 'tagSmall' : 'tag'}
          color={(color && theme.palette[color].shade40) || theme.greys.shade60}
        />
      )}
    </UserThemeConsumer>
  );
}
